import { useMemo, useState, type ReactNode } from "react";
import { ScrollView } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import type { RoadSensePayload } from "@visioni/a3s-reports/client-messages";
import { RoadSenseHero } from "./RoadSenseHero";
import { isReportLive } from "../report-live";
import { TrafficSummary } from "./TrafficSummary";
import { RoadSenseSiteMap } from "./RoadSenseSiteMap";
import { TrafficTrendChart } from "./TrafficTrendChart";
import { TrafficFilters } from "./TrafficFilters";
import {
  applyTrafficFilters,
  defaultTrafficFilters,
  instantsDayBounds,
  movementOptions,
  spanDays,
  type TrafficFilters as TrafficFiltersValue,
} from "../filters/traffic-filters";

interface RoadSenseReportViewProps {
  payload: RoadSensePayload;
  /**
   * Controlled trend filters. When provided (the live report screen drives
   * these from URL params so a filtered view is shareable), the view is fully
   * controlled. When omitted (the dev preview), the view manages its own
   * filter state locally.
   */
  filters?: TrafficFiltersValue;
  onFiltersChange?: (next: TrafficFiltersValue) => void;
  /** Rendered top-right, level with the hero (e.g. the workbook download). */
  headerAction?: ReactNode;
}

/** Fixed height for the chart so the report doesn't stretch with site count. */
const INSIGHTS_PANEL_HEIGHT = 420;

/** The map stretches to the filters card beside it, but never below this. */
const MAP_MIN_HEIGHT = 280;

/** The report is full width, so the filters stop here and the map takes the rest of the row. */
const FILTERS_MAX_WIDTH = 600;

/**
 * The assembled RoadSense report body, rendered purely from a decoded
 * `RoadSensePayload`. Kept free of any data-fetching so it can be driven by the
 * live hook (the report screen) or a static fixture (the dev preview screen)
 * through the same code path.
 *
 * RoadSense's section set is deliberately smaller than WasteSense's: hero, KPI
 * summary, the filters beside the site map, then the full-width trend chart. No incident
 * list, no evidence images, no per-site event logs.
 */
export function RoadSenseReportView({ payload, filters, onFiltersChange, headerAction }: RoadSenseReportViewProps) {
  // Uncontrolled fallback: the dev preview has no URL to hang filter state on,
  // so the view holds it locally, seeded with the data-aware default granularity
  // (lazy init so the payload's span is read once). The live screen passes
  // `filters`/`onChange` and owns this from the URL instead.
  const [localFilters, setLocalFilters] = useState<TrafficFiltersValue>(() =>
    defaultTrafficFilters(spanDays(payload.trend.instants)),
  );
  const activeFilters = filters ?? localFilters;
  const handleFiltersChange = onFiltersChange ?? setLocalFilters;

  const { trend, showWeekendShading } = useMemo(
    () => applyTrafficFilters(payload.trend, activeFilters),
    [payload.trend, activeFilters],
  );

  const isLive = useMemo(
    () => isReportLive(payload.trend.instants, payload.site.site_timezone, new Date()),
    [payload.trend.instants, payload.site.site_timezone],
  );
  const dateBounds = useMemo(() => instantsDayBounds(payload.trend.instants), [payload.trend.instants]);
  const movements = useMemo(() => movementOptions(payload.trend), [payload.trend]);

  // With no coordinates the map would only show a "no location data"
  // placeholder, so drop it; the filters keep their max width.
  const hasMapPoints = useMemo(
    () => payload.scenarios.some((s) => Number.isFinite(s.map_lat) && Number.isFinite(s.map_lon)),
    [payload.scenarios],
  );

  return (
    <View gap="6xl">
      {/* Raised above the insights row (itself raised) so the header action's
          menu draws over the map and chart. */}
      <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="lg" style={styles.header}>
        <View flex={1}>
          <RoadSenseHero meta={payload.report_meta} siteTitle={payload.site.site_title} isLive={isLive} />
        </View>
        {headerAction}
      </View>
      <TrafficSummary cards={payload.traffic_summary} />
      <View gap="md">
        <Text textStyle="md/semibold" color="text">
          Traffic insights
        </Text>
        {/* Raised so the date picker's popover draws over the map and chart:
            RN-web gives each View its own stacking context. */}
        <View flexDirection="row" gap="md" flexWrap="wrap" alignItems="stretch" style={styles.raised}>
          {dateBounds ?
            <View flex={1} minWidth={360} style={[styles.raised, styles.filters]}>
              <TrafficFilters
                filters={activeFilters}
                onChange={handleFiltersChange}
                minDate={dateBounds.min}
                maxDate={dateBounds.max}
                spanDays={spanDays(payload.trend.instants)}
                movements={movements}
              />
            </View>
          : null}
          {hasMapPoints ?
            <View flex={2} minWidth={280} minHeight={MAP_MIN_HEIGHT}>
              <RoadSenseSiteMap site={payload.site} scenarios={payload.scenarios} minHeight={MAP_MIN_HEIGHT} />
            </View>
          : null}
        </View>
        <View height={INSIGHTS_PANEL_HEIGHT}>
          <ChartScroller>
            <TrafficTrendChart
              trend={trend}
              showWeekendShading={showWeekendShading}
              // A subset only when *some but not all* movements are picked.
              // Selecting every chip individually equals "All" (the dashed line
              // still sums everything), so it must read "All", not "Sum".
              movementsAreSubset={
                activeFilters.movements.length > 0 && activeFilters.movements.length < movements.length
              }
            />
          </ChartScroller>
        </View>
      </View>
    </View>
  );
}

/**
 * Keeps the trend chart legible on narrow screens. Below {@link CHART_MIN_WIDTH}
 * the chart would squash its axis labels into an unreadable smear, so instead of
 * shrinking we let the plot hold that minimum width and scroll horizontally. On
 * wider panes `flexGrow` stretches the content to fill the column, so the scroll
 * only ever engages when it's actually needed.
 */
function ChartScroller({ children }: { children: React.ReactNode }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator
      style={styles.chartScroll}
      contentContainerStyle={styles.chartScrollContent}
    >
      <View style={styles.chartScrollInner}>{children}</View>
    </ScrollView>
  );
}

/** Below this the chart scrolls horizontally rather than squashing its labels. */
const CHART_MIN_WIDTH = 360;

const styles = StyleSheet.create(() => ({
  raised: {
    position: "relative",
    zIndex: 1,
  },
  header: {
    position: "relative",
    zIndex: 2,
  },
  filters: {
    maxWidth: FILTERS_MAX_WIDTH,
  },
  chartScroll: {
    flex: 1,
  },
  chartScrollContent: {
    flexGrow: 1,
  },
  chartScrollInner: {
    flex: 1,
    minWidth: CHART_MIN_WIDTH,
    height: "100%",
  },
}));
